/**
 * The margin panel — the **only** thing that draws a margin
 * (`docs/quote-build-refresh.md` §6, board qb-03).
 *
 * The rail's Margin card, the visit popover and the phone and iPad sheets all
 * render this, each with its own scope control around it. Props only — no page
 * hook inside — so step 8 can move it into `builder/margin/` without an edit
 * and step 10 can hand it a package's numbers.
 *
 * Rule 1 holds here: every figure is the server's (`quoteMargin()`). The two
 * things computed on the client are *drawing* — a segment's width as a share
 * of the price, and how far the headline sits from the target in points —
 * because the board draws them and the server has no reason to.
 */
import type { ReactNode } from 'react'
import {
  MARGIN_KIND_LABELS,
  type MarginBlock, type MarginKind, type MarginRow, type VisitMarginLine,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDollars } from '../../lib/format.js'
import { Segmented } from '../../components/primitives.js'
import { KindTile } from '../kind-tile.js'
import { IconChevronUp, IconInfo, IconLock, IconWarn } from '../../frame/icons.js'
import {
  COST_OF_PRICE, FIX_THEM, GROSS_ON, INTERNAL_CHIP, LOWEST_LINES, MARGIN_OF, MARGIN_TITLE,
  MISSING_COST, NET_CHANGE_NOTE, NOT_COUNTED, NOT_FINAL, NO_PRICED_LINES, OPEN_ENDED_REASON,
  PTS_ABOVE, PTS_BELOW, SUBS_UNDER_LABOR, TABLE_HEAD, TARGET_TICK,
} from '../copy.js'

/** The bar's colours are the kind tokens the row tiles already use — four kinds, four colours. */
const KIND_INK: Record<MarginKind, string> = {
  labor: 'rgb(var(--status-quoteLine-labor-fg))',
  material: 'rgb(var(--status-quoteLine-item-fg))',
  equipment: 'rgb(var(--status-quoteLine-equipment-fg))',
  fee: 'rgb(var(--status-quoteLine-deployment-fg))',
}
const KIND_TILE: Record<MarginKind, 'labor' | 'item' | 'equipment' | 'deployment'> = {
  labor: 'labor', material: 'item', equipment: 'equipment', fee: 'deployment',
}

const pct1 = (value: number | null) => (value === null ? '—' : `${value.toFixed(1)}%`)
/** A kind's margin in the table is whole points, as qb-03 prints it — the server's, never re-rounded. */
const pct0 = (value: number | null) => (value === null ? '—' : `${value}%`)
const under = (value: number | null, target: number) => value !== null && value < target

export interface MarginPanelProps {
  block: MarginBlock
  targetPct: number
  /** The label row with the Internal chip. A popover with its own header leaves it off. */
  title?: boolean
  /** Base · Options · All (rail, summary sheet) or Base only · With options (visit). */
  scope?: {
    value: string
    options: { value: string; label: string; disabled?: boolean }[]
    onChange: (value: string) => void
    help?: string
    label: string
  }
  /** Drawn where the scope control would be when there is none — the optional visit's note. */
  note?: ReactNode
  breakdown?: {
    rows: MarginRow[]
    modes: { value: string; label: string }[]
    mode: string
    onMode: (mode: string) => void
    /** Under the rows — the upgrade note with choices (§9.3). */
    after?: ReactNode
  }
  missingCost?: { lines: number; onFix?: () => void }
  notCounted?: { visitId: string; name: string }[]
  /** Whether any line is a sub — the footnote's second sentence (§6.5). */
  hasSubs?: boolean
  /** The visit popover's three lowest lines. */
  lowest?: VisitMarginLine[]
  showLowest?: boolean
  density?: 'rail' | 'popover' | 'sheet'
}

export function MarginPanel({
  block, targetPct, title, scope, note, breakdown, missingCost, notCounted, hasSubs,
  lowest, showLowest, density = 'rail',
}: MarginPanelProps) {
  const missing = missingCost && missingCost.lines > 0
  const priced = block.sold > 0
  const miss = under(block.marginPct, targetPct)

  return (
    <div data-margin-panel={density} className="flex flex-col">
      {title && (
        <div className="mb-3 flex min-h-5 items-center gap-2">
          <span className="text-label uppercase text-text-faint">{MARGIN_TITLE}</span>
          <span className="ml-auto"><InternalChip /></span>
        </div>
      )}

      {missing && (
        <div className="mb-3 flex items-start gap-2 rounded-field border border-warn/30 bg-warn-bg px-3 py-2.5
                        text-[12.5px] leading-[1.4] text-text">
          <IconWarn size={14} className="mt-px shrink-0 text-warn" />
          <span>
            {MISSING_COST(missingCost.lines)}{' '}
            {missingCost.onFix && (
              <button type="button" onClick={missingCost.onFix}
                className="font-semibold text-accent hover:text-accent-dark">
                {FIX_THEM}
              </button>
            )}
          </span>
        </div>
      )}

      <div className="flex items-center gap-2.5">
        <b data-margin-headline className={cn('tnum text-[30px] font-bold leading-none tracking-[-0.02em]',
          miss ? 'text-warn' : 'text-text')}>
          {pct1(block.marginPct)}
        </b>
        {!missing && block.marginPct !== null && (
          <span className="ml-auto"><StatusPill pct={block.marginPct} targetPct={targetPct} /></span>
        )}
      </div>
      <div className="tnum mt-1.5 text-[12.5px] text-text-muted">
        <b className="text-text">{formatDollars(block.gross)}</b> {GROSS_ON} {formatDollars(block.sold)}
      </div>
      {missing && (
        <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-warn">
          <IconWarn size={12} /> {NOT_FINAL}
        </span>
      )}

      {scope && (
        <>
          <div className="mt-3.5">
            <Segmented
              full
              size="large"
              label={scope.label}
              value={scope.value}
              onChange={scope.onChange}
              options={scope.options}
            />
          </div>
          {scope.help && (
            <p className="m-0 mb-3.5 mt-2 text-[11.5px] leading-[1.4] text-text-muted">
              {scope.help}
            </p>
          )}
        </>
      )}
      {note}

      {priced
        ? (
          <>
            <MarginBar block={block} targetPct={targetPct} className={scope ? '' : 'mt-3.5'} />
            <MarginTable block={block} targetPct={targetPct} />
          </>
        )
        : <p className="m-0 mt-3.5 text-[12.5px] text-text-muted">{NO_PRICED_LINES}</p>}

      {showLowest && lowest && lowest.length > 0 && (
        <div className="mt-3 border-t border-line-soft pt-3">
          <span className="text-label uppercase text-text-faint">{LOWEST_LINES}</span>
          <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
            {lowest.map((line) => (
              /* A phone's row is the name and the figure (qb-09 · 2): the sold amount is the popover's. */
              <li key={line.lineId} className={cn('flex items-center',
                density === 'sheet' ? 'h-[34px] gap-2 text-[13px]' : 'gap-2.5 text-[12.5px]')}>
                <KindTile kind={KIND_TILE[line.kind]} size={density === 'sheet' ? 22 : 20} />
                <span className="min-w-0 flex-1 truncate text-text">
                  {line.description}
                  {line.lines > 1 && <span className="text-text-muted"> · {line.lines} lines</span>}
                </span>
                {density !== 'sheet' && <span className="tnum text-text-muted">{formatDollars(line.sold)}</span>}
                <b className={cn('tnum w-12 text-right',
                  under(line.marginPct, targetPct) ? 'text-warn' : 'text-text-muted')}>
                  {pct1(line.marginPct)}
                </b>
              </li>
            ))}
          </ul>
        </div>
      )}

      {breakdown && breakdown.rows.length > 0 && (
        <div className="mt-3 border-t border-line-soft pt-3">
          <div className="mb-1 flex">
            <Segmented
              label="Margin breakdown"
              value={breakdown.mode}
              onChange={breakdown.onMode}
              options={breakdown.modes}
            />
          </div>
          <Breakdown rows={breakdown.rows} targetPct={targetPct} />
          {breakdown.after}
        </div>
      )}

      {notCounted && notCounted.length > 0 && (
        <div className="mt-3 flex flex-col gap-1 border-t border-line-soft pt-3">
          {notCounted.map((row, index) => (
            <p key={row.visitId} className="m-0 flex gap-1.5 text-[11.5px] leading-[1.45] text-text-muted">
              <IconInfo size={13} className="mt-px shrink-0 text-text-faint" />
              <span>
                {NOT_COUNTED} <b className="text-text">{row.name}</b> {OPEN_ENDED_REASON}
                {hasSubs && index === 0 && ` ${SUBS_UNDER_LABOR}`}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

/** The lock-and-word chip every internal card wears (qb-03, qb-07 row 3). */
export function InternalChip() {
  return (
    <span className="inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-[6px]
                     bg-chip-internal px-[7px] text-[10.5px] font-semibold text-text-muted">
      <IconLock size={11} /> {INTERNAL_CHIP}
    </span>
  )
}

/** "▲ 3.1 pts above target" or "⚠ 1.0 pts below target" — words, not only colour (§2 rule 6). */
export function StatusPill({ pct, targetPct }: { pct: number; targetPct: number }) {
  const gap = Math.abs(Math.round((pct - targetPct) * 10) / 10).toFixed(1)
  const above = pct >= targetPct
  return (
    <span className={cn('inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-full pl-1.5 pr-2',
      'text-[11.5px] font-semibold',
      above ? 'bg-accent-soft text-accent-dark' : 'bg-warn-bg text-warn')}>
      {above ? <IconChevronUp size={12} /> : <IconWarn size={12} />}
      {above ? PTS_ABOVE(gap) : PTS_BELOW(gap)}
    </span>
  )
}

/**
 * One bar = one dollar of price (§6.2): the costs by kind, then what is left,
 * and the target as a tick where the costs should stop. One flex row of
 * `<i>`s — not a chart library.
 */
function MarginBar({ block, targetPct, className }: {
  block: MarginBlock; targetPct: number; className?: string
}) {
  const miss = under(block.marginPct, targetPct)
  const tick = `${100 - targetPct}%`
  const shrinking = block.kinds.some((row) => row.cost < 0)
  return (
    <div className={className}>
      <div className="relative h-3.5">
        <span className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap text-[10.5px] font-semibold text-text-muted"
          style={{ left: tick }}>
          {TARGET_TICK(targetPct)}
        </span>
      </div>
      <div data-margin-bar className="relative mt-[3px] h-3">
        <div className="flex h-3 gap-[1.5px] overflow-hidden rounded-[6px] bg-surface">
          {block.kinds.map((row) => (
            <i
              key={row.kind}
              title={`${MARGIN_KIND_LABELS[row.kind]} cost ${(Math.max(0, row.cost) / block.sold * 100).toFixed(1)}% of the price`}
              className="block h-3 shrink-0"
              style={{ width: `${Math.max(0, row.cost) / block.sold * 100}%`, background: KIND_INK[row.kind] }}
            />
          ))}
          <i className="block h-3 min-w-0 flex-1"
            style={{ background: miss ? 'rgb(var(--color-warn))' : 'rgb(var(--color-accent))' }} />
        </div>
        <i aria-hidden data-target-tick className="absolute -top-[5px] -ml-px h-[22px] w-0.5 rounded-[1px] bg-text shadow-[0_0_0_1.5px_rgb(var(--color-surface))]"
          style={{ left: tick }} />
      </div>
      <div className="mt-1.5 flex justify-between text-[11.5px] text-text-muted">
        <span>
          {COST_OF_PRICE.before} <b className="tnum text-text">{(block.cost / block.sold * 100).toFixed(1)}%</b>{' '}
          {COST_OF_PRICE.after}
        </span>
        <span>
          {MARGIN_OF}{' '}
          <b className={cn('tnum', miss ? 'text-warn' : 'text-accent-dark')}>{pct1(block.marginPct)}</b>
        </span>
      </div>
      {shrinking && <p className="m-0 mt-1 text-[11.5px] text-text-muted">{NET_CHANGE_NOTE}</p>}
    </div>
  )
}

const TABLE_GRID = 'grid grid-cols-[1fr_64px_64px_44px] gap-x-1.5'

/** Sold · Cost · Margin per kind, and the Total on a rule (§6.3). */
function MarginTable({ block, targetPct }: { block: MarginBlock; targetPct: number }) {
  return (
    <div className="mt-3.5" data-margin-table>
      <div className={cn(TABLE_GRID, 'border-b border-line-soft pb-[5px]')}>
        <span />
        {[TABLE_HEAD.sold, TABLE_HEAD.cost, TABLE_HEAD.margin].map((word) => (
          <span key={word} className="text-right text-[10px] font-semibold uppercase tracking-[0.06em] text-text-faint">
            {word}
          </span>
        ))}
      </div>
      {block.kinds.map((row) => (
        <div key={row.kind} className={cn(TABLE_GRID, 'tnum h-[26px] items-center text-[12.5px]')}>
          <span className="flex min-w-0 items-center gap-[7px]">
            <i className="inline-block h-2 w-2 shrink-0 rounded-[3px]" style={{ background: KIND_INK[row.kind] }} />
            <span className="truncate text-text">
              {MARGIN_KIND_LABELS[row.kind]}
              {row.kind === 'labor' && row.hours > 0 && (
                <span className="font-normal text-text-faint"> · {formatHoursShort(row.hours)}</span>
              )}
            </span>
          </span>
          <span className="text-right text-text">{formatDollars(row.sold)}</span>
          <span className="text-right text-text-muted">{formatDollars(row.cost)}</span>
          {/* qb-03 prints every kind's figure muted, under target or not: only the Total turns amber. */}
          <span className="text-right text-text-muted">
            {pct0(row.marginPoints)}
          </span>
        </div>
      ))}
      <div className={cn(TABLE_GRID, 'tnum mt-0.5 h-[30px] items-center border-t border-line-soft text-[12.5px] font-bold')}>
        <span>{TABLE_HEAD.total}</span>
        <span className="text-right">{formatDollars(block.sold)}</span>
        <span className="text-right">{formatDollars(block.cost)}</span>
        <span className={cn('text-right', under(block.marginPct, targetPct) ? 'text-warn' : 'text-text')}>
          {pct1(block.marginPct)}
        </span>
      </div>
    </div>
  )
}

/** "110.5 h" — the hours as the board prints them, one decimal only when there is one. */
function formatHoursShort(hours: number): string {
  const rounded = Math.round(hours * 10) / 10
  return `${rounded.toLocaleString('en-US', { maximumFractionDigits: 1 })} h`
}

/**
 * By visit / by division / by choice: each name gets the whole row, its bar
 * on the line below (qb-07 row 7 — never a fixed 92px column with an ellipsis).
 */
function Breakdown({ rows, targetPct }: { rows: MarginRow[]; targetPct: number }) {
  const widest = Math.max(1, ...rows.map((row) => row.sold))
  return (
    <div>
      {rows.map((row) => {
        const miss = under(row.marginPct, targetPct)
        return (
          <div key={row.id} className="py-1.5">
            <div className="flex items-baseline gap-2 text-[12.5px]">
              <span className="min-w-0 flex-1 truncate text-text">{row.name}</span>
              <b className="tnum text-text">{formatDollars(row.sold)}</b>
              <span className={cn('tnum w-12 text-right',
                miss ? 'font-semibold text-warn' : 'font-medium text-text-muted')}>
                {pct1(row.marginPct)}
              </span>
            </div>
            <div className="mt-[5px] h-1 overflow-hidden rounded-[2px] bg-line-soft">
              <i className="block h-1 rounded-[2px]"
                style={{
                  width: `${Math.max(0, row.sold) / widest * 100}%`,
                  background: miss ? 'rgb(var(--color-warn))' : 'rgb(var(--color-accent))',
                }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}
